import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Mic, Sparkles, Crown, Lock, Info, Play, Square, Loader2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VoiceSelector Component - Premium Voice Toggle for Reel/Video Creation
 * 
 * Features:
 * - Standard/Premium voice toggle
 * - Tier-gated access (Pro+ for Premium)
 * - Founder bypass
 * - Beginner-friendly labels (no provider names)
 * - Voice preview playback (3-5 second samples)
 */
const VoiceSelector = ({
  selectedVoice,
  onVoiceChange,
  voiceMode = 'standard',
  onVoiceModeChange,
  disabled = false,
  showCompact = false
}) => {
  const { token, user } = useAuth();
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Preview playback state
  const [playingVoice, setPlayingVoice] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(null);
  const audioRef = useRef(null);
  const previewCacheRef = useRef(new Map()); // Cache blob URLs
  
  const tier = user?.tier || 'starter';
  const isFounder = user?.is_founder;
  const canUsePremium = isFounder || ['pro', 'elite', 'agency', 'studio'].includes(tier);

  // Fetch voice capabilities
  useEffect(() => {
    const fetchCapabilities = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/voice/capabilities`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCapabilities(response.data);
      } catch (error) {
        console.error('Failed to fetch voice capabilities:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCapabilities();
  }, [token]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      // Revoke cached blob URLs on unmount
      previewCacheRef.current.forEach(url => URL.revokeObjectURL(url));
      previewCacheRef.current.clear();
    };
  }, []);

  // Handle preview playback
  const handlePreview = useCallback(async (voiceId, e) => {
    e?.stopPropagation();
    
    // If already playing this voice, stop it
    if (playingVoice === voiceId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingVoice(null);
      return;
    }
    
    // Stop any currently playing preview
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    // Check cache first
    const cacheKey = `${voiceId}_${voiceMode}`;
    let blobUrl = previewCacheRef.current.get(cacheKey);
    
    if (!blobUrl) {
      // Fetch and cache the preview
      setLoadingPreview(voiceId);
      try {
        const response = await axios.get(`${API}/voice/preview/${voiceId}`, {
          params: { voice_mode: voiceMode },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        
        blobUrl = URL.createObjectURL(response.data);
        previewCacheRef.current.set(cacheKey, blobUrl);
      } catch (error) {
        console.error('Failed to fetch voice preview:', error);
        setLoadingPreview(null);
        return;
      }
      setLoadingPreview(null);
    }
    
    // Create or reuse audio element
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener('ended', () => {
        setPlayingVoice(null);
      });
      audioRef.current.addEventListener('error', () => {
        setPlayingVoice(null);
      });
    }
    
    audioRef.current.src = blobUrl;
    audioRef.current.play().catch(err => {
      console.error('Failed to play preview:', err);
      setPlayingVoice(null);
    });
    setPlayingVoice(voiceId);
  }, [playingVoice, voiceMode, token]);

  // Clear preview cache when voice mode changes
  useEffect(() => {
    // Stop current playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingVoice(null);
  }, [voiceMode]);

  // Get voices based on mode
  const getVoices = () => {
    if (!capabilities) {
      // Default voices while loading
      return [
        { id: 'warm_narrator', label: 'Warm Narrator', description: 'Friendly and approachable tone' },
        { id: 'energetic_host', label: 'Energetic Host', description: 'Dynamic and upbeat delivery' },
        { id: 'calm_guide', label: 'Calm Guide', description: 'Soothing and reassuring voice' },
        { id: 'confident_presenter', label: 'Confident Presenter', description: 'Professional and authoritative' },
      ];
    }
    
    if (voiceMode === 'premium' && capabilities.premium_available) {
      return capabilities.premium_voices || [];
    }
    return capabilities.standard_voices || [];
  };

  const voices = getVoices();
  const premiumAvailable = capabilities?.premium_available && canUsePremium;

  // Handle premium toggle
  const handlePremiumToggle = (enabled) => {
    if (enabled && !canUsePremium) {
      // Non-eligible user trying to enable premium - show education
      return;
    }
    onVoiceModeChange?.(enabled ? 'premium' : 'standard');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-4 bg-zinc-800 rounded w-24"></div>
        <div className="grid grid-cols-2 gap-2">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-16 bg-zinc-800 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  // Compact mode for smaller UI areas
  if (showCompact) {
    return (
      <div className="space-y-3">
        {/* Premium Toggle - Only show for eligible tiers */}
        {canUsePremium && (
          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-amber-200">Premium Voice</span>
            </div>
            <Switch
              checked={voiceMode === 'premium'}
              onCheckedChange={handlePremiumToggle}
              disabled={disabled || !premiumAvailable}
              data-testid="premium-voice-toggle"
            />
          </div>
        )}
        
        {/* Voice Select with Preview Button */}
        <div className="flex items-center gap-2">
          <select
            value={selectedVoice}
            onChange={(e) => onVoiceChange(e.target.value)}
            disabled={disabled}
            className="flex-1 p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl text-sm focus:border-primary outline-none"
            data-testid="voice-select-compact"
          >
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
          
          {/* Compact Preview Button */}
          <button
            onClick={(e) => handlePreview(selectedVoice, e)}
            disabled={disabled || loadingPreview === selectedVoice}
            className={`p-3 rounded-xl transition-all ${
              playingVoice === selectedVoice
                ? 'bg-primary text-white'
                : 'bg-zinc-900/50 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700'
            }`}
            data-testid="voice-preview-compact"
            title={playingVoice === selectedVoice ? 'Stop preview' : 'Play preview'}
          >
            {loadingPreview === selectedVoice ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : playingVoice === selectedVoice ? (
              <Square className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Premium Voice Toggle Section */}
      <div className="flex items-center justify-between">
        <Label className="text-zinc-400 flex items-center gap-2">
          <Mic className="w-4 h-4" />
          Voice Selection
        </Label>
        
        {/* Premium Badge/Toggle */}
        {canUsePremium ? (
          <div className="flex items-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${voiceMode === 'premium' ? 'text-amber-400' : 'text-zinc-500'}`}>
                      {voiceMode === 'premium' ? 'Premium' : 'Standard'}
                    </span>
                    <Switch
                      checked={voiceMode === 'premium'}
                      onCheckedChange={handlePremiumToggle}
                      disabled={disabled || !premiumAvailable}
                      data-testid="premium-voice-toggle"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="bg-zinc-900 border-zinc-800">
                  <p className="text-xs">
                    {voiceMode === 'premium' 
                      ? 'Ultra-high quality narration' 
                      : 'Standard quality narration'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {voiceMode === 'premium' && (
              <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Premium
              </span>
            )}
          </div>
        ) : (
          /* Non-eligible user - Show upgrade education */
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 opacity-60">
                  <Lock className="w-3 h-3 text-zinc-500" />
                  <span className="text-xs text-zinc-500">Premium Voice</span>
                  <Info className="w-3 h-3 text-zinc-600" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" className="bg-zinc-900 border-zinc-800 max-w-xs">
                <p className="text-xs text-zinc-300">
                  Premium Voice offers ultra-high quality narration with more natural speech patterns.
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  Available with Pro and higher plans.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Voice Options Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {voices.map((v) => (
          <div
            key={v.id}
            className={`relative p-3 rounded-xl border text-left transition-all ${
              selectedVoice === v.id
                ? voiceMode === 'premium'
                  ? 'bg-amber-500/10 border-amber-500/50 text-white'
                  : 'bg-primary/10 border-primary text-white'
                : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid={`voice-option-${v.id}`}
          >
            <button
              onClick={() => !disabled && onVoiceChange(v.id)}
              disabled={disabled}
              className="w-full text-left"
            >
              <p className="font-medium text-sm flex items-center gap-1 pr-8">
                {v.label}
                {voiceMode === 'premium' && selectedVoice === v.id && (
                  <Sparkles className="w-3 h-3 text-amber-400" />
                )}
              </p>
              <p className="text-xs opacity-60 mt-0.5 line-clamp-1">{v.description}</p>
            </button>
            
            {/* Preview Button */}
            <button
              onClick={(e) => handlePreview(v.id, e)}
              disabled={disabled || loadingPreview === v.id}
              className={`absolute top-2 right-2 p-1.5 rounded-lg transition-all ${
                playingVoice === v.id
                  ? 'bg-primary text-white'
                  : 'bg-zinc-800/80 text-zinc-400 hover:text-white hover:bg-zinc-700'
              }`}
              data-testid={`voice-preview-${v.id}`}
              title={playingVoice === v.id ? 'Stop preview' : 'Play preview'}
            >
              {loadingPreview === v.id ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : playingVoice === v.id ? (
                <Square className="w-3.5 h-3.5" />
              ) : (
                <Play className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Founder Badge */}
      {isFounder && (
        <p className="text-xs text-amber-400 flex items-center gap-1">
          <Crown className="w-3 h-3" />
          Founder: Premium voice access included
        </p>
      )}
      
      {/* Premium unavailable notice */}
      {voiceMode === 'premium' && !premiumAvailable && canUsePremium && (
        <p className="text-xs text-zinc-500 flex items-center gap-1">
          <Info className="w-3 h-3" />
          Premium voice temporarily unavailable. Standard voice will be used.
        </p>
      )}
    </div>
  );
};

export default VoiceSelector;
